import type { ModuleDataDto } from "./setupApi";

export interface DoubtSession {
  accessToken: string | null;
  refreshToken?: string | null;
  gradeId: number | null;
  /** MDS grade for subscription status API (Android parity). */
  mdsGradeId?: number | null;
  /** Tenant key from program/profile (often a UUID); used by DC POST bodies and headers. */
  tenantId: string | null;
  /**
   * Numeric tenant id for DC GET `availableFreeDoubts` (server binds `Long`).
   * When `tenant_id` is a UUID, this comes from `vertical_id` on program/profile.
   */
  dcNumericTenantId: string | null;
  subtenantId?: number | null;
  firstName: string | null;
  /** Last name from accounts (used for avatar initials fallback). */
  lastName: string | null;
  /** Profile photo raw value (URL or icon hint) from profilesData.user_account_details. */
  profilePhoto: string | null;
  uamId: number | null;
  programId?: string | null;
  /** MDS exam id for subscription-status POST (matches native / oneapp API). */
  mdsExamId?: number | null;
  qbId?: number | null;
  studentTimeZone?: string | null;
  /** User phone ISD from profile, e.g. "+91" or "+971". */
  isdCode?: string | null;
  /** Optional logout handler injected by host app for avatar dropdown. */
  handleLogOut?: () => void;
}

export const defaultDoubtSession: DoubtSession = {
  accessToken: null,
  refreshToken: null,
  gradeId: null,
  mdsGradeId: null,
  tenantId: null,
  dcNumericTenantId: null,
  subtenantId: null,
  firstName: null,
  lastName: null,
  profilePhoto: null,
  uamId: null,
  programId: null,
  mdsExamId: null,
  qbId: null,
  studentTimeZone: null,
  isdCode: null,
};

export function mapModuleDataToDoubtSession(d: ModuleDataDto): DoubtSession {
  const program =
    d.enrolledPrograms?.find((p) => p.isDefault) ?? d.enrolledPrograms?.[0];
  const profiles = d.profilesData?.unified_account_details?.profiles;
  const profile = profiles?.find((p) => p.is_default_profile) ?? profiles?.[0];
  const ua = d.accountsData?.unified_accounts?.[0];
  const userAccountDetails = d.profilesData?.unified_account_details
    ?.user_account_details as
    | {
        first_name?: string;
        last_name?: string;
        profile_photo?: string | null;
        isd_code?: string;
      }
    | undefined;

  const rawGrade = Number(program?.mdsGradeId ?? program?.gradeId ?? 0);
  const rawMds = program?.mdsGradeId != null ? Number(program.mdsGradeId) : NaN;
  const rawSub =
    program?.subtenant_id != null ? Number(program.subtenant_id) : NaN;

  const rawExam = program?.mdsExamId != null ? Number(program.mdsExamId) : NaN;

  const tenantFromProgram = program?.tenant_id;
  const tenantFromProfile = profile?.tenant_id;
  const tenantResolved = tenantFromProgram ?? tenantFromProfile;
  const tenantId =
    tenantResolved != null && String(tenantResolved).trim() !== ""
      ? String(tenantResolved)
      : null;

  const rawTenantKey = tenantId ?? "";
  const verticalFromProgram =
    program?.vertical_id != null ? Number(program.vertical_id) : NaN;
  const verticalFromProfile =
    profile?.vertical_id != null ? Number(profile.vertical_id) : NaN;
  const verticalResolved =
    Number.isFinite(verticalFromProgram) && verticalFromProgram > 0
      ? verticalFromProgram
      : Number.isFinite(verticalFromProfile) && verticalFromProfile > 0
        ? verticalFromProfile
        : null;

  let dcNumericTenantId: string | null = null;
  if (/^\d+$/.test(rawTenantKey)) {
    dcNumericTenantId = rawTenantKey;
  } else if (verticalResolved != null) {
    dcNumericTenantId = String(verticalResolved);
  }

  return {
    accessToken: d.accessToken,
    refreshToken: d.refreshToken,
    gradeId: Number.isFinite(rawGrade) && rawGrade > 0 ? rawGrade : null,
    mdsGradeId: Number.isFinite(rawMds) && rawMds > 0 ? rawMds : null,
    tenantId,
    dcNumericTenantId,
    subtenantId: Number.isFinite(rawSub) ? rawSub : null,
    firstName: userAccountDetails?.first_name ?? ua?.first_name ?? null,
    lastName: userAccountDetails?.last_name ?? ua?.last_name ?? null,
    profilePhoto:
      userAccountDetails?.profile_photo ?? ua?.user_account_photo ?? null,
    uamId: profile?.uam_id ?? null,
    programId: program?.program_id ?? null,
    mdsExamId: Number.isFinite(rawExam) && rawExam > 0 ? rawExam : null,
    qbId: d.qbId ?? null,
    studentTimeZone: d.studentTimeZone ?? null,
    isdCode: userAccountDetails?.isd_code ?? null,
  };
}
